import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getFranchiseOEMProgramVersion, getFranchiseOEMReference, getSanitatedLocationOEMs } from "@/Common/helpers/OEMs";

// Defaults
import { MakeMetrics } from "@PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useProgramVersionDetails } from "@/Common/hooks/Metrics";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Components
import MetricsTiers from "./MetricsTiers";

// MUI
import { Divider, Fade, Grid, Stack } from "@mui/material";

const isDisabledReference = ({ readOnly = false, metricsOptions = [], references = [] }) =>
	readOnly || !metricsOptions?.length || !references?.length;

const getReferenceIdentifier = (area = "") => (area === "individualMakeMetrics" ? "individualReferences" : "storeReferences");
const getStoreMetricsMargin = (innerMetrics = null) => (innerMetrics?.individualMakeMetrics ? 0 : 2);

const getUpdatedTierValues = (tier, input = "Percent") => {
	const missed = { value: 0, input };
	const attained = { value: 0, input };
	return { ...tier, missed, attained };
};

const getMappedInputTiers = (tiers = [], input = "Percent") => tiers.map((tier) => getUpdatedTierValues(tier, input));

const getUsableInput = (tiers = []) => tiers?.[0]?.missed?.input || "Percent";

const mapModelOEMVersion = (model, args) => {
	const { make } = args ?? {};
	const { oemReference = "", version = "" } = getFranchiseOEMProgramVersion({ ...args, franchise: make });
	return {
		...model,
		oemReference,
		version,
	};
};

export default function MetricsLayer(props) {
	const location = useSelector((state) => state.location.location);
	const locationOEMs = getSanitatedLocationOEMs(location?.branchesOEMs);

	const {
		make = "",
		oemReference = "",
		version = "",
		metrics = null,
		isService = false,
		emptyError = false,
		readOnly = false,
		useOnlyIndividualMetrics = false,
		useOnlyStoreMetrics = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	const showSwitch = !useOnlyStoreMetrics;

	// Hooks
	const department = isService ? "SERVICE" : "SALES";
	const localOEMReference = getFranchiseOEMReference({ franchise: make, oemReference, locationOEMs });
	const { data: programDetails, isLoading: isLoadingDetails } = useProgramVersionDetails({
		program: "CSI",
		oemReference: localOEMReference,
		department,
		version,
	});

	const { details = undefined } = programDetails ?? {};
	const { metrics: metricsOptions = [], individualReferences = [], storeReferences = [] } = details ?? {};

	// Control to send update actions
	const wasUpdated = useRef(false);
	const onMetricsUpdate = (updatedMetrics) => {
		const isValid = Boolean(isValidIndividuals && isValidStore);
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			const { oemReference: programOEMReference, version: programVersion } = programDetails ?? {};
			wasUpdated.current = false;
			const updatedModel = mapModelOEMVersion(updatedMetrics, {
				make,
				locationOEMs,
				oemReference: programOEMReference,
				version: programVersion,
			});
			onActionUpdate(updatedModel);
		}
	};

	// States
	const [innerMetrics, setMetrics] = useCallbackState(null, onMetricsUpdate);
	const [isValidIndividuals, setIsValidIndividuals] = useState(true);
	const [isValidStore, setIsValidStore] = useState(true);
	const [globalInput, setGlobalInput] = useState("");

	const handleUpdateMetrics = (area, value) => {
		const updated = { ...innerMetrics };
		updated[area] = value;

		const isNotReference = !updated[area]?.["reference"];
		const referenceToUpdate = getReferenceIdentifier(area);
		const updatedReference = get(details, [referenceToUpdate, "0", "reference"], "");

		if (area === "evaluateStoreMetrics") {
			updated["storeMakeMetrics"] = MakeMetrics(updatedReference);
			const individualsInput = getUsableInput(updated?.individualMakeMetrics?.tiers);
			const storeTiers = getMappedInputTiers(updated?.storeMakeMetrics?.tiers, individualsInput);
			updated["storeMakeMetrics"]["tiers"] = [...storeTiers];
			setIsValidStore(true);
		} else if (isNotReference) {
			updated[area]["reference"] = updatedReference;
		}

		wasUpdated.current = true;
		setMetrics(updated);
	};

	const handleUpdateSyncInputs = (input) => {
		if (showSwitch && innerMetrics?.evaluateStoreMetrics) {
			const updated = { ...innerMetrics };

			if (innerMetrics?.individualMakeMetrics) {
				const individualTiers = getMappedInputTiers(innerMetrics?.individualMakeMetrics?.tiers, input);
				updated["individualMakeMetrics"]["tiers"] = [...individualTiers];
			}
			if (innerMetrics?.storeMakeMetrics) {
				const storeTiers = getMappedInputTiers(innerMetrics?.storeMakeMetrics?.tiers, input);
				updated["storeMakeMetrics"]["tiers"] = [...storeTiers];
			}
			setMetrics(updated);
			setGlobalInput(input);
		}
		wasUpdated.current = false;
	};

	useEffect(() => {
		setMetrics(clone(metrics));
		return () => {
			setMetrics(null);
			setIsValidIndividuals(true);
			setIsValidStore(true);
			setGlobalInput("");
			wasUpdated.current = false;
		};
	}, [metrics, setMetrics]);

	const isIndividualsDisabled = isDisabledReference({ readOnly, metricsOptions, references: individualReferences });
	const isStoreDisabled = isDisabledReference({ readOnly, metricsOptions, references: storeReferences });
	const totalMetricsOptions = metricsOptions?.length || 0;

	return (
		<Grid container item xs={12} position={"relative"}>
			{!useOnlyStoreMetrics && (
				<Grid item xs={12} mt={2}>
					{Boolean(innerMetrics?.individualMakeMetrics) && (
						<MetricsTiers
							index={"individualMakeMetrics"}
							title={"Individual Metrics"}
							make={make}
							globalInput={globalInput}
							makeMetrics={get(innerMetrics, "individualMakeMetrics", null)}
							references={individualReferences ?? []}
							options={metricsOptions ?? []}
							totalOptions={totalMetricsOptions}
							emptyError={emptyError}
							loadingOptions={isLoadingDetails}
							loadingReferences={isLoadingDetails}
							readOnly={isIndividualsDisabled}
							onActionInputUpdate={handleUpdateSyncInputs}
							onActionUpdate={handleUpdateMetrics}
							onActionValidation={(value) => setIsValidIndividuals(value)}
						/>
					)}
				</Grid>
			)}

			{Boolean(showSwitch) && (
				<Grid item xs={12} mt={2} mb={2.8}>
					<Divider className="!border-disabled" sx={{ margin: "10px 0" }} />
					<Stack position={"absolute"} style={{ right: 0 }}>
						<CustomizedSwitches
							textLeft
							checked={Boolean(innerMetrics?.evaluateStoreMetrics)}
							disabled={isStoreDisabled}
							textChecked={"Evaluate on store metrics"}
							textUnChecked={"Evaluate on store metrics"}
							onChange={(value) => handleUpdateMetrics("evaluateStoreMetrics", value)}
							tooltip={{
								title: "Include Store's CSI Performance in addition to individual's CSI performance.",
								placement: "left",
								key: "customer-satisfaction-evaluate-tooltip",
							}}
						/>
					</Stack>
				</Grid>
			)}

			{!useOnlyIndividualMetrics && (
				<Fade in={true}>
					<Grid item xs={12} mt={getStoreMetricsMargin(innerMetrics)}>
						{Boolean(innerMetrics?.evaluateStoreMetrics || useOnlyStoreMetrics) && (
							<MetricsTiers
								index={"storeMakeMetrics"}
								title={"Store Metrics"}
								make={make}
								globalInput={globalInput}
								makeMetrics={get(innerMetrics, "storeMakeMetrics", null)}
								references={storeReferences ?? []}
								options={metricsOptions || []}
								totalOptions={totalMetricsOptions}
								emptyError={emptyError}
								hidePercentOfTotal={showSwitch}
								loadingOptions={isLoadingDetails}
								loadingReferences={isLoadingDetails}
								readOnly={isStoreDisabled}
								onActionInputUpdate={handleUpdateSyncInputs}
								onActionUpdate={handleUpdateMetrics}
								onActionValidation={(value) => setIsValidStore(value)}
							/>
						)}
					</Grid>
				</Fade>
			)}
		</Grid>
	);
}
MetricsLayer.propTypes = {
	make: PropTypes.string,
	oemReference: PropTypes.string,
	version: PropTypes.string,
	metrics: PropTypes.object,
	isService: PropTypes.bool,
	emptyError: PropTypes.bool,
	readOnly: PropTypes.bool,
	useOnlyIndividualMetrics: PropTypes.bool,
	useOnlyStoreMetrics: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
